import { useEffect, useState } from "react";
import api from "@/lib/api";
import { GARDIENS, getGardien } from "@/data/gardiens";
import { Sparkles } from "lucide-react";

const FAMILIAR_GARDIENS = GARDIENS.filter((g) => g.familiar);
const de = (name) => (/^[AEIOUYÉÈÊÀÂÎÔ]/i.test(name) ? `d'${name}` : `de ${name}`);

function Tile({ g, owned, big }) {
  return (
    <div data-testid={`familiar-tile-${g.id}`} data-owned={owned ? "true" : "false"}
      className={`rounded-3xl p-4 text-center border-4 ${owned ? "bg-white border-amber-100 soft-in" : "bg-[#F1EDE6] border-transparent"}`}>
      <div className={`${big ? "w-32 h-32 sm:w-36 sm:h-36" : "w-24 h-24"} mx-auto rounded-full overflow-hidden`} style={owned ? { boxShadow: `0 0 0 4px ${g.color}44` } : undefined}>
        <img src={g.familiar.image} alt={owned ? g.familiar.name : "Familier à découvrir"}
          className="w-full h-full object-cover" style={owned ? undefined : { filter: "blur(7px) grayscale(1)", opacity: 0.45, transform: "scale(1.15)" }} />
      </div>
      {owned ? (
        <>
          <p className={`font-serif-display ${big ? "text-2xl" : "text-xl"} mt-3 text-gray-800`}>{g.familiar.name}</p>
          <p className="font-patient text-gray-500 leading-snug">{g.familiar.title}</p>
          <p className="text-xs mt-1 font-semibold" style={{ color: g.color }}>Compagnon {de(g.name)}</p>
        </>
      ) : (
        <p className={`font-serif-display ${big ? "text-2xl" : "text-xl"} mt-3 text-gray-400`}>?</p>
      )}
    </div>
  );
}

export function FamiliarAlbumGrid({ url, big = false }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url).then((r) => setData(r.data)).catch(() => setData(false)); }, [url]);
  if (!data) return null;
  const owned = new Set(data.unlocked.map((f) => f.id));
  const order = [...data.unlocked.map((f) => getGardien(f.id)), ...FAMILIAR_GARDIENS.filter((g) => !owned.has(g.id))];
  return (
    <div data-testid="familiar-album">
      <p className={`font-patient ${big ? "text-2xl" : "text-lg"} text-gray-600 mb-2`} data-testid="familiar-album-count">
        {owned.size} familier{owned.size > 1 ? "s" : ""} sur {data.total}
      </p>
      <div className="h-3 rounded-full bg-amber-100 overflow-hidden mb-3">
        <div className="h-full bg-amber-400 rounded-full transition-[width] duration-700" style={{ width: `${(owned.size / data.total) * 100}%` }} />
      </div>
      <p className={`font-patient ${big ? "text-xl" : "text-base"} text-gray-500 mb-6 flex items-center gap-2`} data-testid="familiar-album-next">
        <Sparkles className="w-5 h-5 text-amber-500" />
        {data.next_in ? `Encore ${data.next_in} activité${data.next_in > 1 ? "s" : ""} avant le prochain familier` : "Tous les familiers sont réunis, bravo !"}
      </p>
      <div className={`grid gap-4 ${big ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"}`}>
        {order.map((g) => <Tile key={g.id} g={g} owned={owned.has(g.id)} big={big} />)}
      </div>
    </div>
  );
}

export default function FamiliarAlbum({ patientId }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="patient-familiar-album">
      <h1 className="font-serif-display italic text-4xl sm:text-5xl text-gray-800 mb-2">L'album des familiers</h1>
      <p className="font-patient text-xl text-gray-500 mb-6">Toutes les 5 activités, un nouveau petit compagnon vous rejoint.</p>
      <FamiliarAlbumGrid url={`/patients/${patientId}/familiars`} big />
    </div>
  );
}

export function NewFamiliarCard({ id }) {
  const g = getGardien(id);
  if (!g.familiar) return null;
  return (
    <div className="mb-8 flex items-center gap-5 rounded-3xl bg-white border-4 border-amber-200 px-6 py-5 text-left soft-in" data-testid="new-familiar-card">
      <img src={g.familiar.image} alt={g.familiar.name} className="w-24 h-24 rounded-full object-cover gentle-float shrink-0" style={{ boxShadow: `0 0 0 4px ${g.color}55` }} />
      <span>
        <span className="block text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold">Un nouveau familier vous rejoint !</span>
        <span className="block font-serif-display text-3xl text-gray-800 mt-1" data-testid="new-familiar-name">{g.familiar.name}</span>
        <span className="block font-patient text-lg text-gray-500">{g.familiar.title}, compagnon {de(g.name)}</span>
      </span>
    </div>
  );
}
